import { createContext, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

import type {
	AuthMethodLookupResponse,
	KeystoneConfig,
	KeystoneSession,
	KeystoneSessionStatus,
	KeystoneUser,
	RuntimeConfig,
	SignInOptions,
	SignInWithPasswordOptions,
	SignOutOptions,
} from './types';

export const KeystoneContext = createContext<KeystoneSession>({
	status: 'idle',
	user: null,
	error: null,
	getToken: async () => '',
	lookupOrgMethod: async () => ({ method: null, methods: [], org: null, defaultAuthDomain: null }),
	runtimeConfig: null,
	signIn: () => {},
	signInWithPassword: async () => {},
	signOut: async () => {},
});

export type KeystoneProviderProps = {
	config: KeystoneConfig;
	children: ReactNode;
};

// --- PKCE helpers (S256) ---

const generateCodeVerifier = (): string => {
	const array = new Uint8Array(32);
	crypto.getRandomValues(array);
	return base64UrlEncode(array);
};

const base64UrlEncode = (buffer: Uint8Array): string => {
	let binary = '';
	for (let i = 0; i < buffer.length; i++) {
		binary += String.fromCharCode(buffer[i]);
	}
	return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

const generateCodeChallenge = async (verifier: string): Promise<string> => {
	const encoder = new TextEncoder();
	const data = encoder.encode(verifier);
	const digest = await crypto.subtle.digest('SHA-256', data);
	return base64UrlEncode(new Uint8Array(digest));
};

const VERIFIER_KEY = 'ks_pkce_verifier';
const STATE_KEY = 'ks_pkce_state';
// Set by signOut() so autoSignIn doesn't immediately re-trigger in the same tab.
// Cleared on tab close (sessionStorage semantics) or on explicit signIn().
const SIGNED_OUT_FLAG = 'ks_signed_out';
// Stamped before a recovery redirect, so a round trip that keeps failing settles instead of looping.
const REAUTH_MARKER = 'ks_reauth_at';
const REAUTH_LOOP_WINDOW_MS = 60_000;

// --- Helpers ---

const parseJwtUser = (jwt: string): KeystoneUser => {
	const payload = JSON.parse(atob(jwt.split('.')[1]));

	const appRoles = payload.appRoles ?? payload.app_roles ?? payload.roles ?? [];
	const capabilities = payload.capabilities ?? payload.caps ?? [];
	const displayName = payload.displayName ?? payload.display_name ?? payload.name ?? '';
	const orgId = payload.orgId ?? payload.org_id ?? payload.oid ?? null;
	const email = payload.email ?? payload.preferred_username ?? payload.upn ?? '';

	return { sub: payload.sub ?? '', email, displayName, orgId, appRoles, capabilities };
};

// Refresh this far ahead of exp, so a once-a-minute poll gets several attempts before it lapses.
const REFRESH_SKEW_MS = 5 * 60_000;
// Browsers throttle a hidden tab's timers to roughly this, which is why freshness is polled.
const FRESHNESS_POLL_MS = 60_000;

const decodeTokenExpMs = (jwt: string): number | null => {
	try {
		const payload = JSON.parse(atob(jwt.split('.')[1]));
		return typeof payload.exp === 'number' ? payload.exp * 1000 : null;
	} catch {
		return null;
	}
};

// A token whose exp cannot be read is treated as expired -- safer to re-auth than to trust it.
const isTokenExpired = (jwt: string, skewMs = 0): boolean => {
	const expMs = decodeTokenExpMs(jwt);
	return expMs === null ? true : Date.now() >= expMs - skewMs;
};

const extractToken = (data: Record<string, unknown>): string | null => {
	const jwt = data.token ?? data.access_token ?? data.accessToken;
	return typeof jwt === 'string' ? jwt : null;
};

const buildAuthorizeUrl = async (config: KeystoneConfig, opts: SignInOptions = {}): Promise<string> => {
	const verifier = generateCodeVerifier();
	const challenge = await generateCodeChallenge(verifier);
	const state = btoa(JSON.stringify({ returnTo: window.location.pathname }));

	sessionStorage.setItem(VERIFIER_KEY, verifier);
	sessionStorage.setItem(STATE_KEY, state);

	const params = new URLSearchParams({
		client_id: config.clientId,
		redirect_uri: config.redirectUri,
		response_type: 'code',
		code_challenge: challenge,
		code_challenge_method: 'S256',
		state,
	});

	if (config.targetComponent) {
		params.set('target_component', config.targetComponent);
	}
	if (opts.method) {
		params.set('method', opts.method);
	}
	if (opts.loginHint) {
		params.set('login_hint', opts.loginHint);
	}
	if (opts.domainHint) {
		params.set('domain_hint', opts.domainHint);
	}

	return `${config.keystoneUrl}/oauth/authorize?${params.toString()}`;
};

const lookupAuthMethod = async (keystoneUrl: string, email: string): Promise<AuthMethodLookupResponse> => {
	const url = new URL('/api/v1/auth/method', keystoneUrl);
	url.searchParams.set('email', email);
	const res = await fetch(url.toString(), { method: 'GET' });
	if (!res.ok) throw new Error(`Auth method lookup failed: HTTP ${res.status}`);
	return await res.json() as AuthMethodLookupResponse;
};

/**
 * How to resume the lapsed session this token belonged to, or null when it cannot be resumed
 * without asking. /oauth/authorize defaults to ENTRA, so resuming a GOOGLE user without naming
 * their method would wall them at a sign-in they cannot complete.
 */
const resumeOptions = async (keystoneUrl: string, lapsedToken: string): Promise<SignInOptions | null> => {
	let email: string;
	try {
		({ email } = parseJwtUser(lapsedToken));
	} catch {
		return null;
	}
	if (!email) return null;

	try {
		const { method } = await lookupAuthMethod(keystoneUrl, email);
		// PASSWORD and an unresolved method both need the sign-in screen -- neither can be a redirect.
		return method === 'ENTRA' || method === 'GOOGLE' ? { method, loginHint: email } : null;
	} catch {
		return null;
	}
};

// A missing or unreachable endpoint leaves every flag at its permissive default, so an older
// keystone never hides a sign-in option that actually works.
const fetchRuntimeConfig = async (keystoneUrl: string): Promise<RuntimeConfig | null> => {
	try {
		const res = await fetch(new URL('/.well-known/runtime-config', keystoneUrl).toString(), { credentials: 'omit' });
		if (!res.ok) return null;
		const data = await res.json() as Partial<RuntimeConfig>;
		return { fedcm: data.fedcm === true, google: data.google !== false };
	} catch {
		return null;
	}
};

// --- Provider ---

export const KeystoneProvider = (props: KeystoneProviderProps): React.ReactElement => {
	const { config, children } = props;
	const [status, setStatus] = useState<KeystoneSessionStatus>('loading');
	const [user, setUser] = useState<KeystoneUser | null>(null);
	const [token, setToken] = useState<string | null>(null);
	const [error, setError] = useState<Error | null>(null);
	const [runtimeConfig, setRuntimeConfig] = useState<RuntimeConfig | null>(null);

	// Waiters: callers of getToken() that arrived before the token was ready
	const tokenRef = useRef<string | null>(null);
	const waitersRef = useRef<Array<{ resolve: (t: string) => void; reject: (e: Error) => void }>>([]);
	// Guard against React 18 StrictMode double-mount
	const bootstrapRan = useRef(false);
	const pollRef = useRef<number | null>(null);
	const refreshPromise = useRef<Promise<void> | null>(null);
	// focus and visibilitychange both fire on tab re-activation; a second redirect would overwrite
	// the PKCE verifier the first one is already navigating with.
	const recovering = useRef(false);

	// Declared as functions (hoisted) because they are mutually recursive:
	// checkFreshness -> refreshNow -> applyToken.
	function applyToken (jwt: string): void {
		localStorage.setItem('ks_token', jwt);
		sessionStorage.removeItem(REAUTH_MARKER);
		// Set eagerly, not just via the [token] effect: a getToken() caller awaiting a refresh
		// reads this ref the moment the round trip resolves, before React has committed.
		tokenRef.current = jwt;
		setUser(parseJwtUser(jwt));
		setToken(jwt);
		setStatus('ready');
	}

	function settleSignedOut (): void {
		setToken(null);
		setUser(null);
		setStatus('signed-out');
	}

	// `lapsedToken` is the token the session died holding, or null for a visitor arriving cold.
	function handleAuthFailure (lapsedToken: string | null): void {
		if (recovering.current) return;
		localStorage.removeItem('ks_token');
		tokenRef.current = null;

		// A session that only lapsed is rebuilt silently through the 30-day SSO cookie; autoSignIn
		// governs the cold visitor, not the return of a session this browser already held.
		const stamped = Number(sessionStorage.getItem(REAUTH_MARKER) ?? 0);
		const looping = stamped > 0 && Date.now() - stamped < REAUTH_LOOP_WINDOW_MS;
		const recoverable = (lapsedToken !== null || config.autoSignIn === true) && !sessionStorage.getItem(SIGNED_OUT_FLAG) && !looping;

		if (recoverable) {
			recovering.current = true;
			sessionStorage.setItem(REAUTH_MARKER, String(Date.now()));
			setStatus('signing-in');
			void resumeSession(lapsedToken);
			return;
		}

		settleSignedOut();
	}

	async function resumeSession (lapsedToken: string | null): Promise<void> {
		// A cold visitor has no identity to resume, so autoSignIn redirects exactly as it always did.
		const opts = lapsedToken === null ? {} : await resumeOptions(config.keystoneUrl, lapsedToken);
		if (opts === null) {
			sessionStorage.removeItem(REAUTH_MARKER);
			recovering.current = false;
			settleSignedOut();
			return;
		}
		window.location.href = await buildAuthorizeUrl(config, opts);
	}

	// Coalesced: the poll, a focus event and any number of getToken() callers share one round trip
	// rather than racing several refreshes against the same token.
	function refreshNow (): Promise<void> {
		if (refreshPromise.current) return refreshPromise.current;
		const current = tokenRef.current;
		if (!current) return Promise.resolve();

		refreshPromise.current = (async () => {
			try {
				const res = await fetch(`${config.keystoneUrl}/api/v1/auth/refresh`, {
					method: 'POST',
					headers: { Authorization: `Bearer ${current}` },
				});
				if (!res.ok) { handleAuthFailure(current); return; }
				const jwt = extractToken(await res.json() as Record<string, unknown>);
				if (!jwt) { handleAuthFailure(current); return; }
				applyToken(jwt);
			} catch {
				// Transient network error -- the freshness poll retries on its next tick.
			}
		})().finally(() => { refreshPromise.current = null; });

		return refreshPromise.current;
	}

	function checkFreshness (): void {
		const current = tokenRef.current;
		if (!current) return;
		if (isTokenExpired(current)) handleAuthFailure(current);
		else if (isTokenExpired(current, REFRESH_SKEW_MS)) void refreshNow();
	}

	useEffect(() => {
		tokenRef.current = token;
		if (token && waitersRef.current.length > 0) {
			const pending = waitersRef.current.splice(0);
			pending.forEach((w) => w.resolve(token));
		}
	}, [token]);

	// A single long timer never survives a slept or throttled tab, so freshness is polled instead,
	// with focus and visibility as the immediate backstop on wake.
	useEffect(() => {
		pollRef.current = window.setInterval(checkFreshness, FRESHNESS_POLL_MS);
		window.addEventListener('focus', checkFreshness);
		document.addEventListener('visibilitychange', checkFreshness);
		return () => {
			window.removeEventListener('focus', checkFreshness);
			document.removeEventListener('visibilitychange', checkFreshness);
			if (pollRef.current !== null) { window.clearInterval(pollRef.current); pollRef.current = null; }
		};
	}, []);

	useEffect(() => {
		let live = true;
		void fetchRuntimeConfig(config.keystoneUrl).then((cfg) => { if (live) setRuntimeConfig(cfg); });
		return () => { live = false; };
	}, [config.keystoneUrl]);

	useEffect(() => {
		if (bootstrapRan.current) return;
		bootstrapRan.current = true;

		// 1. Check for persisted token in localStorage. An expired token is
		// discarded so we fall through to a (silent) re-authorize instead of
		// handing a stale token to callers -- otherwise a reload just reuses it.
		let lapsedToken: string | null = null;
		const persisted = localStorage.getItem('ks_token');
		if (persisted && !isTokenExpired(persisted)) {
			try {
				applyToken(persisted);
				return;
			} catch (_e) {
				localStorage.removeItem('ks_token');
			}
		} else if (persisted) {
			localStorage.removeItem('ks_token');
			// This browser held a session that has since lapsed -- step 4 resumes it rather than
			// dropping a signed-in user on the sign-in screen.
			lapsedToken = persisted;
		}

		// 2. Check for OAuth callback code in URL
		const params = new URLSearchParams(window.location.search);
		const code = params.get('code');
		const stateParam = params.get('state');

		if (code && stateParam) {
			const verifier = sessionStorage.getItem(VERIFIER_KEY);
			const storedState = sessionStorage.getItem(STATE_KEY);
			sessionStorage.removeItem(VERIFIER_KEY);
			sessionStorage.removeItem(STATE_KEY);
			// Clear the signed-out flag on successful callback
			sessionStorage.removeItem(SIGNED_OUT_FLAG);

			if (!verifier) {
				setError(new Error('PKCE verifier not found — session may have expired. Please sign in again.'));
				setStatus('error');
				return;
			}

			if (storedState && stateParam !== storedState) {
				setError(new Error('OAuth state mismatch — possible CSRF, aborting sign-in.'));
				setStatus('error');
				return;
			}

			setStatus('exchanging');
			fetch(`${config.keystoneUrl}/oauth/token`, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({
					grant_type: 'authorization_code',
					code,
					state: stateParam,
					code_verifier: verifier,
					redirect_uri: config.redirectUri,
					client_id: config.clientId,
					...(config.targetComponent ? { target_component: config.targetComponent } : {}),
				}),
			})
				.then(async (res) => {
					if (!res.ok) {
						const text = await res.text();
						throw new Error(`Token exchange failed: ${res.status} ${text}`);
					}
					return res.json();
				})
				.then((data: Record<string, unknown>) => {
					const jwt = extractToken(data);
					if (!jwt) {
						throw new Error(`Unexpected token response shape: ${JSON.stringify(Object.keys(data))}`);
					}
					applyToken(jwt);

					const cleanUrl = window.location.pathname;
					window.history.replaceState({}, '', cleanUrl === '/auth/callback' ? '/r' : cleanUrl);
				})
				.catch((err: Error) => {
					console.error('[keystone-react] Token exchange failed:', err.message);
					setError(err);
					setStatus('error');
				});
			return;
		}

		// 3. Check for OAuth error response in URL (server rejected /authorize)
		const errorParam = params.get('error');
		if (errorParam) {
			const errorDesc = params.get('error_description');
			const url = new URL(window.location.href);
			url.searchParams.delete('error');
			url.searchParams.delete('error_description');
			url.searchParams.delete('state');
			window.history.replaceState({}, '', url.toString());
			setError(new Error(`${errorParam}${errorDesc ? `: ${errorDesc}` : ''}`));
			setStatus('error');
			return;
		}

		// 4. No token, no code — rebuild a lapsed session, auto-sign-in, or stay signed out.
		handleAuthFailure(lapsedToken);
	}, []);

	const getToken = useCallback(async (): Promise<string> => {
		const current = tokenRef.current;
		if (current && !isTokenExpired(current, REFRESH_SKEW_MS)) return current;

		// Renew on read rather than handing back a token that dies in flight. The heartbeat
		// normally gets there first; this covers the call that races it.
		if (current) {
			await refreshNow();
			if (tokenRef.current) return tokenRef.current;
		}

		return new Promise<string>((resolve, reject) => {
			if (tokenRef.current) {
				resolve(tokenRef.current);
				return;
			}

			const timeout = setTimeout(() => {
				const idx = waitersRef.current.findIndex((w) => w.resolve === resolve);
				if (idx >= 0) waitersRef.current.splice(idx, 1);
				reject(new Error('Timed out waiting for auth token (30s)'));
			}, 30_000);

			waitersRef.current.push({
				resolve: (t: string) => { clearTimeout(timeout); resolve(t); },
				reject: (e: Error) => { clearTimeout(timeout); reject(e); },
			});
		});
	}, []);

	const lookupOrgMethod = useCallback(
		(email: string): Promise<AuthMethodLookupResponse> => lookupAuthMethod(config.keystoneUrl, email),
		[config.keystoneUrl],
	);

	const signIn = useCallback((opts: SignInOptions = {}): void => {
		setStatus('signing-in');
		// Explicit sign-in supersedes a prior signOut flag
		sessionStorage.removeItem(SIGNED_OUT_FLAG);
		buildAuthorizeUrl(config, opts).then((url) => {
			window.location.href = url;
		});
	}, [config]);

	const signInWithPassword = useCallback(async (opts: SignInWithPasswordOptions): Promise<void> => {
		const { email, password } = opts;
		setStatus('signing-in');
		setError(null);
		sessionStorage.removeItem(SIGNED_OUT_FLAG);

		try {
			// credentials: 'include' so keystone's SSO cookie lands here too and sibling SPAs sign in silently.
			const res = await fetch(new URL('/api/v1/auth/password', config.keystoneUrl).toString(), {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				credentials: 'include',
				body: JSON.stringify({
					email,
					password,
					clientId: config.clientId,
					...(config.targetComponent ? { targetComponent: config.targetComponent } : {}),
				}),
			});
			const data = await res.json() as Record<string, unknown>;
			if (!res.ok) {
				const envelope = data.error as { message?: string } | undefined;
				throw new Error(envelope?.message ?? `Password sign-in failed: HTTP ${res.status}`);
			}
			const jwt = extractToken(data);
			if (!jwt) throw new Error('Password sign-in returned no token.');
			applyToken(jwt);
		} catch (err) {
			const failure = err instanceof Error ? err : new Error(String(err));
			setStatus('signed-out');
			setError(failure);
			throw failure;
		}
	}, [config.clientId, config.keystoneUrl, config.targetComponent]);

	const signOut = useCallback(async (opts: SignOutOptions = {}): Promise<void> => {
		// Capture token before clearing — needed to authenticate the /oauth/logout call
		const currentToken = tokenRef.current;

		// Clear local state
		localStorage.removeItem('ks_token');
		sessionStorage.removeItem(VERIFIER_KEY);
		sessionStorage.removeItem(STATE_KEY);
		sessionStorage.removeItem(REAUTH_MARKER);
		// Prevent autoSignIn from immediately re-triggering in this tab
		sessionStorage.setItem(SIGNED_OUT_FLAG, '1');

		// Revoke the server-side session (best-effort)
		if (currentToken) {
			const url = new URL('/oauth/logout', config.keystoneUrl);
			if (opts.everywhere) url.searchParams.set('everywhere', 'true');
			try {
				await fetch(url.toString(), {
					method: 'POST',
					headers: { Authorization: `Bearer ${currentToken}` },
					credentials: 'include',
				});
			} catch {
				// Best-effort.
			}
		}

		// Full reload ensures clean state — bootstrap re-runs, finds no token,
		// sees SIGNED_OUT_FLAG, and settles to 'signed-out'.
		window.location.href = '/';
	}, [config.keystoneUrl]);

	const session = useMemo<KeystoneSession>(() => ({
		status, user, error, getToken, lookupOrgMethod, runtimeConfig, signIn, signInWithPassword, signOut,
	}), [status, user, error, getToken, lookupOrgMethod, runtimeConfig, signIn, signInWithPassword, signOut]);

	return (
		<KeystoneContext.Provider value={session}>
			{children}
		</KeystoneContext.Provider>
	);
};
